<template>
  <PopupMenu class="notificationsPopup"
             ref="popupMenu"
             @subMenuClose="$emit('close')"
             :horizontalPosition="'left'"
             :verticalPosition="above ? 'above' : 'below'">
    <template #menuOpener>
      <slot></slot>
    </template>
    <template #header>
      <div class="heading">
        <div>
          Notifications
        </div>
        <div class="focusContainer">
          <button v-if="allNotifications.length > 0"
                  class="clearAll"
                  @click.prevent.stop="removeAllConfirm">
            Clear all
          </button>
        </div>
      </div>
    </template>
    <template #default>
      <ul class="items">
        <li v-for="notification in allNotifications" :key="notification.id">
          <NotificationNavListItem :notification="notification"
                                   @click="notificationClicked"
                                   @deleted="notificationDeleted(notification)">
          </NotificationNavListItem>
        </li>
        <li>
          <div class="noNotifications" v-if="allNotifications.length === 0">
            You have no notifications
          </div>
        </li>
      </ul>
    </template>
    <template #footer>
      <div v-if="allNotifications.length">
        <div class="focusContainer">
          <router-link class="footerLink" :to="notificationsRoute" @click="close">
            View all notifications
          </router-link>
        </div>
      </div>
    </template>
  </PopupMenu>
</template>

<script setup lang="ts">
import { computed, ComputedRef, Ref, ref } from 'vue';
import { RouteLocationRaw } from 'vue-router';

import PopupMenu, { PopupMenuApi } from '@/components/dialogs/popupmenu/PopupMenu.vue';
import { useNotifications } from '@/composables/notification/Notifications';
import { Notification } from '@/data/datatypes/Notification';
import NotificationNavListItem from '@/navigation/NotificationNavListItem.vue';
import RouteNames from '@/router/RouteNames';

const { allNotifications } = useNotifications();

interface Props {
  above?: boolean;
}
withDefaults(defineProps<Props>(), {
  above: false,
});

interface Emits {
  (e: 'close'): void,
  (e: 'clearAllNotifications'): void,
  (e: 'notificationClicked'): void,
  (e: 'notificationDeleted'): void,
}
const emit = defineEmits<Emits>();

const popupMenu: Ref<PopupMenuApi | null> = ref(null);

const notificationsRoute: ComputedRef<RouteLocationRaw> = computed(() => {
  return {
    name: RouteNames.NOTIFICATIONS_ROUTE_NAME
  };
});

function close(): void {
  // It is necessary to close the popupMenu manually as it has been populated via slots
  popupMenu.value?.close();
  emit('close');
}

function removeAllConfirm(): void {
  // clear all notifications
  close();
  emit('clearAllNotifications');
}

function notificationClicked(): void {
  // notify clicked
  close();
  emit('notificationClicked');
}

function notificationDeleted(notification: Notification): void {
  if (notification.deleteHandler) {
    notification.deleteHandler();
  }
  emit('notificationDeleted');
}
</script>

<style scoped lang="scss">
.footerLink {
  display: flex;
  padding: 0.8rem 1.25rem 0.5rem 1.75rem;
}

.notificationItem {
  :deep(a.navListItem) {
    // Less padding on the right because the icons add some extra spacing
    padding-right: 1.25rem;
    padding-left: 2rem;
    outline-offset: -2px;
  }
}

.focusContainer {
  // Following two rules are for browsers without focus-visible support
  :focus {
    text-decoration: underline;
    outline: none;
  }
  :focus:not(:focus-visible) {
    text-decoration: none;
  }

  :focus-visible {
    text-decoration: underline;
    outline: none;
  }
}

.notificationsPopup {
  :deep(.popupMenu) {
    max-height: calc(100vh - 9rem);
  }
  :deep(.pmHeader) {
    padding: 1rem 1.75rem;
    background: $colour-background-light;
  }
  .items {
    max-width: 30rem;

    margin: 0;
    padding: 0;
    list-style: none;
  }

  .noNotifications {
    padding: 0.75rem 1.75rem;
  }

  .clearAll {
    color: $colour-text-primary;
    line-height: 1.2rem;
  }

  .clearAll:hover {
    color: $colour-text-primary-emphasis;
  }

  .heading {
    font-weight: $weight-bold;
    font-size: 1rem;
    display: flex;
    flex: 1;
    justify-content: space-between;
  }
}
</style>
